import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, ChevronRight, Minus, Plus, CheckCircle2, Truck, Shield, RotateCcw, Cpu } from 'lucide-react';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = products.find(p => p.slug === slug);
  const category = product ? categories.find(c => c.id === product.categoryId) : null;
  const relatedProducts = product
    ? products.filter(p => p.categoryId === product.categoryId && p.id !== product.id).slice(0, 4)
    : [];

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | EC Pedal Works`;
    }
    window.scrollTo(0, 0);
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <CartDrawer />
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
            <Cpu className="w-20 h-20 text-gray-300 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-8">The product you&apos;re looking for doesn&apos;t exist.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors">
              Back to Shop
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <CartDrawer />

      <main>
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-1.5 text-sm flex-wrap">
              <Link to="/" className="text-gray-500 hover:text-orange-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <Link to="/shop" className="text-gray-500 hover:text-orange-600">Shop</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <Link to={`/shop?category=${category?.slug}`} className="text-gray-500 hover:text-orange-600">
                {category?.name}
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900 font-medium truncate max-w-[220px]">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-6 sm:p-10">
            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.badge && (
                <span className="absolute top-4 left-4 px-3 py-1.5 bg-orange-600 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow">
                  {product.badge}
                </span>
              )}
            </div>

            {/* Product Info */}
            <div>
              <Link
                to={`/shop?category=${category?.slug}`}
                className="inline-flex items-center gap-2 text-orange-600 text-sm font-semibold uppercase tracking-wider hover:text-orange-700 transition-colors mb-3"
              >
                <Cpu className="w-4 h-4" />
                {category?.name}
              </Link>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                {product.name}
              </h1>

              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {product.description}
              </p>

              {/* Price */}
              <div className="flex items-center gap-4 mb-8">
                <span className="text-4xl font-bold text-orange-600">${product.price.toFixed(2)}</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 text-sm font-semibold rounded-full">
                  <CheckCircle2 className="w-4 h-4" />
                  In Stock
                </span>
              </div>

              {/* Key Benefits */}
              {product.keyBenefits && (
                <div className="mb-8">
                  <h3 className="font-bold text-gray-900 mb-3">Key Benefits</h3>
                  <ul className="space-y-2.5">
                    {product.keyBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* How to Use */}
              {product.usage && (
                <div className="mb-8 bg-orange-50 rounded-xl p-5 border border-orange-100">
                  <h3 className="font-bold text-gray-900 mb-2">How to Use</h3>
                  <p className="text-gray-700 text-sm">{product.usage}</p>
                </div>
              )}

              {/* Features */}
              {product.features && (
                <div className="mb-8">
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full border border-gray-200">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    className="p-3 text-gray-600 hover:text-orange-600 hover:bg-gray-50 transition-colors"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-14 text-center text-gray-900 font-semibold text-lg">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className="p-3 text-gray-600 hover:text-orange-600 hover:bg-gray-50 transition-colors"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 font-bold rounded-lg transition-all shadow-sm ${
                    addedToCart
                      ? 'bg-green-600 text-white'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                  <Truck className="w-5 h-5 text-orange-600 mb-1.5" />
                  <span className="text-gray-900 text-xs font-semibold">Free Shipping</span>
                  <span className="text-gray-500 text-[11px]">USPS, 2-4 days</span>
                </div>
                <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                  <Shield className="w-5 h-5 text-orange-600 mb-1.5" />
                  <span className="text-gray-900 text-xs font-semibold">Secure Checkout</span>
                  <span className="text-gray-500 text-[11px]">SSL Protected</span>
                </div>
                <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                  <RotateCcw className="w-5 h-5 text-orange-600 mb-1.5" />
                  <span className="text-gray-900 text-xs font-semibold">30-Day Returns</span>
                  <span className="text-gray-500 text-[11px]">Hassle-free</span>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map(relProduct => (
                  <Link
                    key={relProduct.id}
                    to={`/product/${relProduct.slug}`}
                    className="group bg-white rounded-lg shadow hover:shadow-lg overflow-hidden transition-all"
                  >
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      <img
                        src={relProduct.image}
                        alt={relProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-gray-800 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                        {relProduct.name}
                      </h3>
                      <span className="text-lg font-bold text-orange-600">${relProduct.price.toFixed(2)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
